import { FormattedMessage } from "react-intl";

import { Button } from "@ps-design-system/components/Button";
import { Divider } from "@ps-design-system/components/Divider";
import { Form } from "@ps-design-system/components/Form";
import { Text } from "@ps-design-system/components/Text";

import type { Space } from "../../../../../types/spaces.types";
import { trackFormNavClicked } from "../../utils/your-space.utils";
import { useSpaceDetailsForm } from "../useSpaceDetailsForm";

import { BrandNameField } from "./form-fields/BrandNameField";
import { DescriptionField } from "./form-fields/DescriptionField";
import { SizeField } from "./form-fields/SizeField";
import { SpaceTitleField } from "./form-fields/SpaceTitleField";

type SpaceDetailsFormProps = {
  space: Space;
};

/**
 * The form component for editing space details, including title, brand name,
 * description, and size. It uses the `useSpaceDetailsForm` hook to manage form
 * state and handle submission.
 */
const SpaceDetailsForm = ({ space }: SpaceDetailsFormProps) => {
  const { form, onSave, errorMsg } = useSpaceDetailsForm(space);

  const handleSave = async () => {
    const success = await onSave();
    if (success) {
      trackFormNavClicked("next");
      window.location.href =
        space.status === "DRAFT"
          ? `/your-space/${space._id}/rules`
          : `/your-space/${space._id}`;
    }
  };

  const handleBack = () => trackFormNavClicked("previous");

  return (
    <Form {...form} spacing={0}>
      <SpaceTitleField />

      <Divider spacing={48} />

      <BrandNameField />

      <Divider spacing={48} />

      <DescriptionField />

      <Divider spacing={48} />

      <SizeField spaceCountry={space.country} />

      <Divider spacing={48} />

      <div className="tw-flex tw-justify-between tw-mt-32 tw-items-start">
        <Button
          appearance="white"
          variant="outline-1"
          href={`/your-space/${space._id}`}
          onClick={handleBack}
        >
          <FormattedMessage
            id="hosting.space_details.back_button"
            defaultMessage="Back"
          />
        </Button>

        <div className="tw-flex tw-flex-col tw-items-end tw-gap-8">
          <Button
            appearance="purple"
            disabled={!form.formState.isDirty}
            onClick={handleSave}
            showSpinner={form.formState.isSubmitting}
            testId="space-details-save-button"
          >
            <FormattedMessage
              id="hosting.space_details.save_button"
              defaultMessage="Save"
            />
          </Button>
          {errorMsg && (
            <Text
              typeface="body-1"
              element="p"
              className="tw-text-red-800"
              testId="space-details-submit-error"
            >
              {errorMsg}
            </Text>
          )}
        </div>
      </div>
    </Form>
  );
};

export { SpaceDetailsForm };
